import cloneDeep from 'lodash/cloneDeep';
import { MosaicKey } from 'react-mosaic-component/lib/types';
import { UNPROCESSED_QUERY_BUILDER_TEST_IDS } from 'vet-bones/components';
import { isProduction, processTestIds } from 'vet-bones/utils';
import { QueryBuilderTestIds } from 'vet-bones/utils/queryBuilder';

import { DataSourceType } from 'src/constants/dataSourceTypeDescription';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { CreationType } from 'src/containers/PublishProjectDrawer/types';
import { VirtualGraphOption } from 'src/store/state/dataSources';

const getTestId = (id: string, idx?: number) =>
  isProduction() && id ? `${idx}` : id;

export const getClassItemTestId = (id: string, idx?: number) =>
  `sd-class-item-${getTestId(id, idx)}`;

export const getDatabaseItemTestId = (id: string, idx?: number) =>
  `sd-database-item-${getTestId(id, idx)}`;

export const getDataSourceCardTestId = (id: string, idx?: number) =>
  `sd-data-source-card-${getTestId(id, idx)}`;

export const getDataSourceTypeCardTestId = (id: DataSourceType) =>
  `sd-data-source-type-card-${id}`;

export const getDataSourceStandardOptionTestId = (id: string, idx?: number) =>
  `sd-data-source-default-option-${getTestId(id, idx)}`;

export const getDataSourceCreationPanelAdvancedPropertyTestId = (
  key: string,
  idx?: number
) => `sd-data-source-creation-panel-advanced-property-${getTestId(key, idx)}`;

export const getModelItemTestId = (id: string, idx?: number) =>
  `sd-model-item-${getTestId(id, idx)}`;

export const getMosaicTestId = (id: MosaicKey) => `sd-mosaic-${id}`;

export const getNamedGraphItemTestId = (id: string, idx?: number) =>
  `sd-named-graph-item-${getTestId(id, idx)}`;

export const getNotificationTestId = (action: string) =>
  `sd-notification-${action}`;

export const getNotificationActionButtonTestId = (
  id: string,
  actionText: string
) => `sd-notification-${id}-button-${actionText}`;

export const getResourceItemTestId = (id: string, idx?: number) =>
  `sd-resource-item-${getTestId(id, idx)}`;

export const getVirtualGraphItemTestId = (id: string, idx?: number) =>
  `sd-virtual-graph-item-${getTestId(id, idx)}`;

export const getProjectCardTestId = (id: string, idx?: number) =>
  `sd-project-card-${getTestId(id, idx)}`;

export const getProjectTitleTestId = (id: string, idx?: number) =>
  `sd-project-title-${getTestId(id, idx)}`;

export const getProjectMenuButtonTestId = (id: string, idx?: number) =>
  `sd-project-menu-button-${getTestId(id, idx)}`;

export const getProjectResourceCardTestId = (id: string, idx?: number) =>
  `sd-project-resource-card-${getTestId(id, idx)}`;

export const getProjectResourceCardCheckboxTestId = (
  id: string,
  idx?: number
) => `sd-project-resource-card-checkbox-${getTestId(id, idx)}`;

export const getProjectResourceCardViewPreviewButtonTestId = (
  id: string,
  idx?: number
) => `sd-project-resource-card-view-preview-button-${getTestId(id, idx)}`;

export const getProjectResourceMenuButtonTestId = (id: string, idx?: number) =>
  `sd-project-resource-menu-button-${getTestId(id, idx)}`;

export const getProjectModelCardTestId = (id: string, idx?: number) =>
  `sd-project-model-card-${getTestId(id, idx)}`;

export const getProjectModelMenuButtonTestId = (id: string, idx?: number) =>
  `sd-project-model-menu-button-${getTestId(id, idx)}`;

export const getProjectSidebarSectionTestId = (id: string) =>
  `sd-project-sidebar-section-${id}`;

export const getProjectSidebarMappingItemTestId = (id: string, idx?: number) =>
  `sd-project-sidebar-mapping-item-${getTestId(id, idx)}`;

export const getProjectDiagramEntityDrawerSelectorTestId = (id: string) =>
  `sd-project-diagram-entity-drawer-selector-${id}`;

export const getProjectDiagramEntityDrawerSelectItemTestId = (
  id: string,
  idx?: number
) => `sd-project-diagram-entity-drawer-select-item-${getTestId(id, idx)}`;

export const getProjectDiagramEntityDrawerSelectLinkTestId = (
  id: string,
  type: string,
  idx?: number
) =>
  `sd-project-diagram-entity-drawer-select-link-${type}-${getTestId(id, idx)}`;

export const getProjectDiagramEntityDrawerTableRowTestId = (
  id: string,
  type: string,
  idx?: number
) => `sd-project-diagram-entity-drawer-table-row-${type}-${getTestId(id, idx)}`;

export const getProjectDiagramEntityDrawerTableLinkTestId = (
  id: string,
  type: string,
  idx?: number
) =>
  `sd-project-diagram-entity-drawer-table-link-${type}-${getTestId(id, idx)}`;

export const getProjectDiagramModelListTabTestId = (value: string) =>
  `sd-project-diagram-model-list-tab-${getTestId(value)}`;

export const getProjectDiagramModelTableCellLinkTestId = (
  id: string,
  idx?: number
) => `sd-project-diagram-model-table-cell-link-${getTestId(id, idx)}`;

export const getProjectDiagramModelTableColumnHeaderTestId = (
  id: string,
  idx?: number
) => `sd-project-diagram-model-table-column-header-${getTestId(id, idx)}`;

export const getProjectDiagramModelTableColumnsFilterTestId = (
  id: string,
  idx?: number
) => `sd-project-diagram-model-table-columns-filter-${getTestId(id, idx)}`;

export const getProjectFileUploadPanelColumnLabelTestId = (value: string) =>
  `sd-project-file-upload-panel-column-label-${getTestId(value)}`;

export const getProjectFileUploadPanelPreviewTabTestId = (value: string) =>
  `sd-project-file-upload-panel-preview-tab-${getTestId(value)}`;

export const getProjectVirtualGraphPanelColumnLabelTestId = (value: string) =>
  `sd-project-virtual-graph-panel-column-label-${getTestId(value)}`;

export const getProjectVirtualGraphPanelTableSelectItemTestId = (
  id: string,
  idx?: number
) => `sd-project-virtual-graph-panel-table-select-item-${getTestId(id, idx)}`;

export const getProjectVirtualGraphPanelPropertyTestId = (
  option: VirtualGraphOption
) => `sd-project-virtual-graph-panel-property-${option.replace(/\./g, '_')}`;

export const getProjectVirtualGraphPanelSqlQuotingItemTestId = (item: string) =>
  `sd-project-virtual-graph-panel-sql-quoting-item-${item}`;

export const getProjectVirtualGraphPanelAdvancedPropertyTestId = (
  key: string,
  idx?: number
) => `sd-project-virtual-graph-panel-advanced-property-${getTestId(key, idx)}`;

export const getProjectVirtualGraphPanelPreviewTabTestId = (value: string) =>
  `sd-project-virtual-graph-panel-preview-tab-${getTestId(value)}`;

export const getVirtualGraphAdvancedOptionRemoveTestId = (idx: number) =>
  `sd-virtual-graph-advanced-option-remove-${idx}`;

export const getVirtualGraphAdvancedOptionPropertyTestId = (idx: number) =>
  `sd-virtual-graph-advanced-option-property-${idx}`;

export const getVirtualGraphAdvancedOptionValueTestId = (idx: number) =>
  `sd-virtual-graph-advanced-option-value-${idx}`;

export const getProjectMappingLinkTestId = (id: string, idx?: number) =>
  `sd-project-mapping-link-${getTestId(id, idx)}`;

export const getProjectMappingHeaderTestId = (idx: number) =>
  `sd-project-mapping-header-${idx}`;

export const getProjectMappingFilterMenuItemId = (id: string, idx?: number) =>
  `sd-project-mapping-filter-menu-item-${getTestId(id, idx)}`;

export const getProjectMappingAttributeTestId = (iri: string, idx?: number) =>
  `sd-project-mapping-attribute-${getTestId(iri, idx)}`;

export const getProjectMappingAttributeFieldSelectorTestId = (
  id: string,
  idx?: number
) => `sd-project-mapping-attribute-field-selector-${getTestId(id, idx)}`;

export const getProjectMappingAttributeFieldMenuTestId = (
  id: string,
  idx?: number
) => `sd-project-mapping-attribute-field-menu-${getTestId(id, idx)}`;

export const getProjectMappingRelationshipTestId = (
  iri: string,
  direction: RelationshipDirection,
  idx?: number
) => `sd-project-mapping-relationship-${getTestId(`${iri}-${direction}`, idx)}`;

export const getProjectMappingRelationshipMappingCellTestId = (
  relationshipIri: string,
  relationshipDirection: RelationshipDirection,
  key: string,
  idx?: number
) =>
  `sd-project-mapping-relationship-mapping-cell-${getTestId(
    relationshipIri,
    idx
  )}-${relationshipDirection}-${getTestId(key, idx)}`;

export const getProjectMappingRelationshipMappingMenuTestId = (
  relationshipIri: string,
  relationshipDirection: RelationshipDirection,
  key: string,
  idx?: number
) =>
  `sd-project-mapping-relationship-mapping-menu-${getTestId(
    relationshipIri,
    idx
  )}-${relationshipDirection}-${getTestId(key, idx)}`;

export const getProjectMappingSelectItemTestId = (id: string, idx?: number) =>
  `sd-project-mapping-select-item-${getTestId(id, idx)}`;

export const getProjectMappingCompositeIdentifierItemTestId = (
  id: string,
  idx?: number
) => `sd-project-mapping-composite-identifier-item-${getTestId(id, idx)}`;

export const getPublishProjectDrawerResourceRowTestId = (
  id: string,
  idx?: number
) => `sd-publish-project-drawer-resource-row-${getTestId(id, idx)}`;

export const getPublishProjectDrawerCreationTypeTestId = (type: CreationType) =>
  `sd-publish-project-drawer-creation-type-${type}`;

export const getPublishProjectDrawerModelAliasRowTestId = (
  id: string,
  idx?: number
) => `sd-publish-project-drawer-model-alias-row-id-${getTestId(id, idx)}`;

export const getPublishProjectDrawerModelRowTestId = (
  id: string,
  idx?: number
) => `sd-publish-project-drawer-model-row-id-${getTestId(id, idx)}`;

export const getPublishProjectDrawerNamedGraphDeletionWarningTestId = (
  modelName: string,
  namedGraphIri: string,
  idx?: number
) =>
  `sd-publish-project-drawer-named-graph-deletion-warning-${getTestId(
    `${modelName}-${namedGraphIri}`,
    idx
  )}`;

export const getPublishProjectDrawerShouldPublishCheckboxTestId = (
  id: string,
  idx?: number
) => `sd-publish-project-drawer-should-publish-checkbox-${getTestId(id, idx)}`;

export const getConnectionMenuItemTestId = (id: string, idx?: number) =>
  `sd-connection-menu-item-${getTestId(id, idx)}`;

export const getOverviewDrawerViewItemTestId = (id: string, idx?: number) =>
  `sd-rule-overview-drawer-view-card-${getTestId(id, idx)}`;

export const getColorPickerSwatchButtonTestId = (id: string) =>
  `sd-color-picker-swatch-button-${id}`;

// V2 Resource Dialog test IDs
export const getVgResourceCardTestId = (name: string, idx?: number) =>
  `sd-vg-resource-card-${getTestId(name, idx)}`;

export const getDataSourceTableCheckboxTestId = (
  tableName: string,
  idx?: number
) => `sd-data-source-table-checkbox-${getTestId(tableName, idx)}`;

export const getDataSourceTableLabelTestId = (
  tableName: string,
  idx?: number
) => `sd-data-source-table-label-${getTestId(tableName, idx)}`;

// diagram entity drawer types
export const PROJECT_DIAGRAM_ENTITY_DRAWER_ATTRIBUTE_TYPE = 'attribute';
export const PROJECT_DIAGRAM_ENTITY_DRAWER_CLASS_TYPE = 'class';
export const PROJECT_DIAGRAM_ENTITY_DRAWER_DATATYPES_TYPE = 'datatypes';
export const PROJECT_DIAGRAM_ENTITY_DRAWER_DOMAIN_TYPE = 'domain';
export const PROJECT_DIAGRAM_ENTITY_DRAWER_RANGE_TYPE = 'range';
export const PROJECT_DIAGRAM_ENTITY_DRAWER_RELATIONSHIP_TYPE = 'relationship';
export const PROJECT_DIAGRAM_ENTITY_DRAWER_SENSITIVE_PROPERTY_GROUP =
  'sensitivePropertyGroup';

export const TEST_IDS = {
  // global
  class: {
    selector: '',
  },
  collapsibleBlock: {
    body: '',
    collapseButton: '',
  },
  colorPicker: {
    menuButton: '',
  },
  connection: {
    card: '',
    form: {
      password: '',
      submitButton: '',
      title: '',
    },
    menu: '',
  },
  portalConnect: {
    icon: '',
    menu: '',
    tooltipContent: '',
  },
  createMappingDialog: {
    classSelector: '',
    classSelectorFilter: '',
  },
  database: {
    card: '',
    selector: '',
    selectorFilter: '',
    selectorCreateItem: '',
    selectorCreateItemError: '',
  },
  dialog: {
    alert: {
      okButton: '',
    },
    closeIcon: '',
    confirmation: {
      noButton: '',
      yesButton: '',
    },
    newProject: {
      voicebox: '',
      blankProject: '',
      importModel: '',
      connectResource: '',
    },
    prompt: {
      cancelButton: '',
      submitButton: '',
      valueField: '',
    },
  },
  drawer: {
    backdrop: '',
    header: '',
  },
  dropzone: {
    container: '',
    fileInput: '',
  },
  itemQueryList: {
    filterButton: '',
  },
  model: {
    card: '',
    selector: '',
  },
  namedGraph: {
    append: {
      selector: '',
    },
    replace: {
      selector: '',
    },
  },
  propertyType: {
    selector: '',
  },
  resource: {
    selector: '',
  },
  selector: {
    clear: '',
  },
  virtualGraph: {
    selector: '',
  },

  // containers
  appNavbar: {
    container: '',
    logo: '',
  },
  createProjectOverlay: {
    steps: {
      backButton: '',
      stepButton: getTestId,
      submitButton: '',
    },
    projectScopingPanel: {
      nameInput: '',
      descriptionTextarea: '',
      nextButton: '',
      submitButton: '',
    },
    competencyQuestionsPanel: {
      addQuestionButton: '',
      clearAllButton: '',
      generateQuestionsButton: '',
      questionInput: getTestId,
      questionRemoveButton: '',
      questionsCount: '',
      submitButton: '',
    },
  },
  home: {
    createMenuButton: '',
    createProjectButton: '',
    importProject: {
      button: '',
      fileInput: '',
    },
    projectCards: '',
  },
  projectDiagram: {
    container: '',
    contextMenu: {
      container: '',
      delete: '',
      edit: '',
      mapping: '',
      newMapping: '',
      preview: '',
      settings: '',
    },
    entityDrawer: {
      addButton: '',
      attributesTable: '',
      back: {
        button: '',
        placeholder: '',
        tooltip: '',
      },
      cancelButton: '',
      classGeospatialTable: '',
      classRelationshipsTable: '',
      createButton: '',
      createRuleButton: '',
      description: '',
      editableIriButton: '',
      editButton: '',
      incomingCheckbox: '',
      name: '',
      outgoingCheckbox: '',
      relationshipTable: '',
      removeButton: '',
      ruleButton: '',
      selectorFilter: '',
      tableAddRow: '',
      tabs: {
        basic: '',
        hierarchy: '',
        relationships: '',
        attributes: '',
        geospatial: '',
      },
      unlinkButton: '',
      updateButton: '',
      sensitivePropertyGroups: {
        toggle: '',
        createButton: '',
        filterInput: '',
        checkbox: '',
        attributeList: '',
      },
    },
    modelList: {
      classes: {
        container: '',
      },
      relationships: {
        container: '',
      },
      attributes: {
        container: '',
      },
      table: {
        columnMenu: {
          hide: '',
          sortAsc: '',
          sortDesc: '',
        },
        columnsFilter: {
          button: '',
          checkbox: '',
        },
        rowFilter: {
          clear: '',
          input: '',
        },
      },
    },
    navbar: {
      addClass: '',
      addMapping: '',
      addRelationship: '',
      deleteSelection: '',
      graphViewButton: '',
      listViewButton: '',
      redrawLayout: '',
      redo: '',
      rules: '',
      searchButton: '',
      searchInput: '',
      searchClear: '',
      undo: '',
    },
    resourceDrawer: {
      details: '',
      fields: '',
      mapping: {
        add: '',
        container: '',
      },
      preview: '',
      type: '',
    },
  },
  projectFileUploadPanel: {
    form: {
      separator: '',
      quote: '',
      escape: '',
    },
    rowCount: '',
    table: '',
  },
  projectMapping: {
    annotations: {
      expand: '',
      selectorCell: '',
      selector: '',
    },
    classMapping: {
      list: '',
    },
    editAttributesButton: '',
    field: {
      menu: {
        button: '',
        duplicate: '',
        remove: '',
      },
      selector: '',
    },
    filterMenu: {
      attributeMenu: '',
      relationshipMenu: '',
      filterButton: '',
      clearButton: '',
      filterField: '',
      selectAllButton: '',
    },
    primaryIdentifier: {
      composite: {
        container: '',
        preview: '',
        tags: '',
      },
      editableIriButton: '',
      previewContainer: '',
      resetButton: '',
      selectorCell: '',
    },
    relationships: {
      mappings: {
        classSelector: '',
      },
      menu: {
        button: '',
        duplicate: '',
        remove: '',
      },
    },
    suggestion: {
      button: '',
    },
  },
  projectCard: {
    updateDate: '',
  },
  projectMenu: {
    deleteItem: '',
    duplicateItem: '',
    migrateItem: '',
  },
  projectEndpointDialog: {
    proceedButton: '',
    switchConnection: '',
  },
  projectMappingSettingsDrawer: {
    container: '',
    mappingName: '',
    resetButton: '',
    setClassType: '',
    uniqueKeySets: '',
  },
  projectModelDialog: {
    body: '',
    backButton: '',
    submitButton: '',
    localModelPanel: {
      nameInput: '',
      namespaceInput: '',
    },
    modelTypePanel: {
      importModel: {
        proceedButton: '',
        switchConnection: '',
      },
      localModel: {
        proceedButton: '',
      },
      uploadModel: {
        proceedButton: '',
      },
    },
    uploadModelPanel: {
      fileUploadInput: '',
      nameInput: '',
      namedGraphInput: '',
    },
    validation: {
      comboModels: '',
      largeModelLimit: '',
    },
  },
  projectNavbar: {
    back: '',
    export: {
      publish: '',
      zip: '',
    },
    more: '',
    settings: '',
    toggle: {
      mappingViewButton: '',
      modelViewButton: '',
    },
  },
  projectResourceDialog: {
    backButton: '',
    createAndMapButton: '',
    createButton: '',
    dataSourceCreationPanel: {
      advancedPropertiesButton: '',
      advancedPropertiesEditor: '',
      advancedProperties: {
        addButton: '',
        newName: '',
        newValue: '',
      },
    },
    dataSourcePanel: {
      connectionInfo: '',
      dataSourceInfo: '',
      typeInfo: '',
    },
    resourceTypePanel: {
      proceedButton: '',
      switchConnection: '',
    },
    saveButton: '',
  },
  projectResourceDialogV2: {
    connectionSelection: {
      container: '',
      newConnectionCard: '',
    },
    addDataSource: {
      tableTree: '',
    },
  },
  projectRuleDetailDrawer: {
    error: '',
    cancelButton: '',
    container: '',
    deleteButton: '',
    ifClause: '',
    reasoningWarning: '',
    submitButton: '',
    thenClause: '',
  },
  projectSettingsDrawer: {
    cancelButton: '',
    container: '',
    customPropertyTypeWarning: '',
    description: '',
    dataNamespace: '',
    modelNamespace: getTestId,
    name: '',
    submitButton: '',
    useAdvancedSettings: '',
  },
  projectSidebar: {
    addButton: '',
    filterClearButton: '',
    filterInput: '',
    mappingMenu: {
      button: '',
      deleteButton: '',
      deleteConfirmation: '',
      settingsButton: '',
    },
    modelMenu: {
      deleteButton: '',
      hideModelButton: '',
      refreshButton: '',
      renameButton: '',
      settingsButton: '',
      targetCheckmark: '',
      targetButton: '',
    },
    questionCard: {
      title: '',
      cancelButton: '',
      editButton: '',
      evaluateButton: '',
      deleteButton: '',
      menuButton: '',
      moreDetailsButton: '',
      textarea: '',
      toggleButton: '',
    },
    refreshButton: '',
    resourceCard: {
      title: '',
      fileUploadIcon: '',
      virtualGraphIcon: '',
    },
    resourceMenu: {
      deleteButton: '',
      viewPreviewButton: '',
    },
    sectionToggleButton: '',
  },
  projectVirtualGraphPanel: {
    advancedProperties: {
      addButton: '',
      button: '',
      newKey: '',
      newValue: '',
      editor: '',
    },
    resourceProperties: {
      accordion: '',
      sqlQuotingConvention: '',
      sqlFunctions: '',
      percentEncode: '',
      advancedOptions: {
        accordion: '',
        addButton: '',
        propertyInput: '',
        valueInput: '',
      },
    },
    customQuery: {
      field: '',
      queryRadioButton: '',
      queryTab: '',
      radioGroup: '',
      tableRadioButton: '',
      tableTab: '',
      validationButton: '',
    },
    preview: {
      emptyResults: '',
      error: '',
      refreshButton: '',
      rowCount: '',
      table: '',
    },
    replaceDataSource: '',
    resourceName: '',
    settingsButton: '',
    singleTableInput: '',
    tableSelect: '',
    tableSelectionHeader: '',
  },
  publishProjectDrawer: {
    connection: {
      container: '',
      serverSelect: '',
    },
    database: {
      container: '',
      modelAlias: {
        collapseButton: '',
        toggle: '',
      },
      voiceboxSwitch: '',
      voiceboxQuestionsRefresh: '',
      voiceboxQuestionsSwitch: '',
    },
    footer: {
      backButton: '',
      container: '',
      exportSwitch: '',
      nextToAliasButton: '',
      nextToModelButton: '',
      nextToMappingButton: '',
      publishButton: '',
      warning: '',
    },
    mapping: {
      collapseButton: '',
      collapseBody: '',
      container: '',
      csvNamedGraphInput: '',
      upload: '',
      vgNameInput: '',
    },
    model: {
      checkbox: '',
      collapseButton: '',
      consolidateRadioButton: '',
      container: '',
      modelNameInput: '',
      multipleModelsRadioButton: '',
      namedGraphIriInput: '',
      rules: {
        toggle: '',
      },
      sensitivePropertyGroups: {
        toggle: '',
        create: '',
        replace: '',
        append: '',
        select: '',
        textInput: '',
        collapseButton: '',
      },
    },
    modelAlias: {
      container: '',
    },
    openInExplorer: {
      container: '',
      link: '',
    },
    openInVoicebox: {
      link: '',
    },
    stepper: '',
    useAdvancedSettings: '',
  },
  resourceSuggestionsDialog: {
    cancelButton: '',
    container: '',
    nextButton: '',
  },
  ruleOverviewDrawer: {
    header: '',
    createButton: '',
    emptyCreateButton: '',
  },
  voicebox: {
    footer: {
      accept: '',
      container: '',
      reject: '',
    },
    summary: {
      actions: {
        unansweredQuestions: getTestId,
        unusedResources: getTestId,
        unlinkedConcepts: getTestId,
        addQuestions: '',
        addDataset: '',
      },
      issuesCountBadge: '',
      more: {
        unansweredQuestions: '',
        unusedResources: '',
        unlinkedConcepts: '',
        addQuestions: '',
        addDataset: '',
      },
      toggleButton: '',
      refreshButton: '',
    },
  },
};
processTestIds(TEST_IDS, '-', 'sd');

const baseQueryBuilderTestIds = cloneDeep(UNPROCESSED_QUERY_BUILDER_TEST_IDS);
processTestIds(baseQueryBuilderTestIds, '-', 'sd-rules-query-builder');

export const RULES_QUERY_BUILDER_TEST_IDS: QueryBuilderTestIds = {
  ...baseQueryBuilderTestIds,
  classSelectorItem: (id: string, idx?: number) =>
    `sd-rules-query-builder-class-item-${getTestId(id, idx)}`,
  classSelectorSubItem: (
    id: string,
    subId: string,
    idx?: number,
    subIdx?: number
  ) =>
    `sd-rules-query-builder-class-item-${getTestId(id, idx)}-sub-${getTestId(
      subId,
      subIdx
    )}`,
  instanceSelectorItem: (id: string, idx?: number) =>
    `sd-rules-query-builder-instance-item-${getTestId(id, idx)}`,
  operatorSelectorItem: (id: string, idx?: number) =>
    `sd-rules-query-builder-operator-item-${getTestId(id, idx)}`,
  operatorSelectorSubItem: (
    id: string,
    subId: string,
    idx?: number,
    subIdx?: number
  ) =>
    `sd-rules-query-builder-operator-item-${getTestId(id, idx)}-sub-${getTestId(
      subId,
      subIdx
    )}`,
  propertySelectorItem: (id: string, idx?: number) =>
    `sd-rules-query-builder-property-item-${getTestId(id, idx)}`,
  propertySelectorSubItem: (
    id: string,
    subId: string,
    idx?: number,
    subIdx?: number
  ) =>
    `sd-rules-query-builder-property-item-${getTestId(id, idx)}-sub-${getTestId(
      subId,
      subIdx
    )}`,
};
